import React from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { CompactDevelopmentSeries } from '../../services/compactDevelopmentService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const tonsFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

// Chart start year: line charts from baseline (2023)
const LINE_CHART_START_YEAR = 2023;

interface CompactDevelopmentChartsProps {
  location: string;
  series: CompactDevelopmentSeries | null;
  loading?: boolean;
}

const CompactDevelopmentCharts: React.FC<CompactDevelopmentChartsProps> = ({
  location,
  series,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="transit-service-charts-section">
        <h4 className="transit-service-charts-title">Compact Development Over Time</h4>
        <p className="transit-service-charts-empty">Loading compact development data for {location}…</p>
      </div>
    );
  }

  if (!series || series.rows.length === 0) {
    return (
      <div className="transit-service-charts-section">
        <h4 className="transit-service-charts-title">Compact Development Over Time</h4>
        <p className="transit-service-charts-empty">
          Housing and GHG data are not available for {location} to compute compact development impacts.
        </p>
      </div>
    );
  }

  const rows = series.rows;
  const lineRows = rows.filter((r) => r.year >= LINE_CHART_START_YEAR);
  const lineLabels = lineRows.map((r) => String(r.year));

  // Cumulative = running sum of annual from 2023 so 2023 matches annual chart, 2024 = 2023 + 2024, etc.
  const cumStatewideAvoided: number[] = [];
  const cumOOB: number[] = [];
  const cumDensity: number[] = [];
  const cumAdded: number[] = [];
  const cumLocalNet: number[] = [];
  const cumNet: number[] = [];
  let sStateAvoided = 0;
  let sOOB = 0;
  let sDensity = 0;
  let sAdded = 0;
  let sLocalNet = 0;
  let sNet = 0;
  lineRows.forEach((r) => {
    sStateAvoided += r.statewideAvoidedAnnual;
    sOOB += r.outOfBoundarySavingsAnnual;
    sDensity += r.densitySavingsAnnual;
    sAdded += r.addedLocalGhgAnnual;
    sLocalNet += r.netLocalAnnual;
    sNet += r.netStatewideAnnual;
    cumStatewideAvoided.push(sStateAvoided);
    cumOOB.push(-sOOB);
    cumDensity.push(-sDensity);
    cumAdded.push(sAdded);
    cumLocalNet.push(sLocalNet);
    cumNet.push(sNet);
  });

  const lineOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? tonsFormatter.format(Number(ctx.parsed.y)) : '—'}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => (typeof value === 'number' ? tonsFormatter.format(value) : value),
        },
      },
    },
  };

  const accountingModeLabel =
    series.mode === 'workbook_exact'
      ? 'Accounting: workbook exact (all policy new units counted in avoided/added terms).'
      : 'Accounting: incremental baseline (only units above BAU counted in avoided/added terms).';

  return (
    <div className="transit-service-charts-section">
      <h4 className="transit-service-charts-title">Compact Development Over Time</h4>
      <p className="transit-service-charts-subtitle">
        Policy impacts include <strong>out-of-boundary savings</strong> (building in lower-VMT locations avoids emissions elsewhere in the state). {accountingModeLabel}
      </p>

      <div className="transit-service-charts-grid">
        <div className="transit-service-chart-card">
          <h5>New housing units under policy and BAU</h5>
          <p style={{ fontSize: '0.75rem', marginBottom: 8, color: '#6b7280' }}>
            New units only (no existing stock). Annual new units under BAU vs under policy.
          </p>
          <div className="transit-service-chart-container">
            <Line
              data={{
                labels: lineLabels,
                datasets: [
                  {
                    label: 'New housing units under policy',
                    data: lineRows.map((r) => r.newUnitsPolicy),
                    borderColor: '#059669',
                    backgroundColor: 'rgba(5, 150, 105, 0.18)',
                    tension: 0.2,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'New BAU',
                    data: lineRows.map((r) => r.newUnitsBau),
                    borderColor: '#6b7280',
                    backgroundColor: 'rgba(107, 114, 128, 0.15)',
                    tension: 0.2,
                    fill: false,
                    pointRadius: 2,
                  },
                ],
              }}
              options={lineOptions}
            />
          </div>
        </div>

        <div className="transit-service-chart-card">
          <h5>Annual statewide impact components (MT CO₂e)</h5>
          <p style={{ fontSize: '0.75rem', marginBottom: 8, color: '#6b7280' }}>
            <strong>Statewide avoided</strong> = units above BAU × state avg GHG per home (large negative). <strong>Statewide net</strong> = Statewide avoided + local added − density avoided. <strong>Local net</strong> = local added − density avoided. Avoided shown as negative; added as positive.
          </p>
          <div className="transit-service-chart-container">
            <Line
              data={{
                labels: lineLabels,
                datasets: [
                  {
                    label: 'Statewide avoided',
                    data: lineRows.map((r) => r.statewideAvoidedAnnual),
                    borderColor: '#0369a1',
                    backgroundColor: 'rgba(3, 105, 161, 0.15)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Density avoided',
                    data: lineRows.map((r) => -r.densitySavingsAnnual),
                    borderColor: '#d97706',
                    backgroundColor: 'rgba(217, 119, 6, 0.12)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Added local emissions',
                    data: lineRows.map((r) => r.addedLocalGhgAnnual),
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Local net',
                    data: lineRows.map((r) => r.netLocalAnnual),
                    borderColor: '#7c3aed',
                    backgroundColor: 'rgba(124, 58, 237, 0.12)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Net (statewide)',
                    data: lineRows.map((r) => r.netStatewideAnnual),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                ...lineOptions,
                scales: {
                  ...lineOptions.scales,
                  y: {
                    ...lineOptions.scales?.y,
                    ticks: {
                      callback: (value) =>
                        typeof value === 'number'
                          ? (value >= 0 ? '' : '−') + tonsFormatter.format(Math.abs(value))
                          : value,
                    },
                  },
                },
                plugins: {
                  ...lineOptions.plugins,
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const full: Record<string, string> = {
                          'OOB avoided': 'Out-of-boundary avoided (MT CO₂e)',
                          'Density avoided': 'Density avoided (MT CO₂e)',
                          'Added local emissions': 'Added local emissions (MT CO₂e)',
                          'Net reduction (statewide)': 'Net reduction statewide (MT CO₂e)',
                        };
                        const name = full[ctx.dataset.label as string] ?? ctx.dataset.label;
                        const y = ctx.parsed.y != null ? Number(ctx.parsed.y) : null;
                        const fmt =
                          y != null ? (y >= 0 ? '' : '−') + tonsFormatter.format(Math.abs(y)) : '—';
                        return `${name}: ${fmt}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="transit-service-chart-card">
          <h5>Cumulative statewide impact components (MT CO₂e)</h5>
          <p style={{ fontSize: '0.75rem', marginBottom: 8, color: '#6b7280' }}>
            Running sum of annual components from 2023. 2023 = same as annual chart; each later year adds that year’s annual value. <strong>Statewide avoided</strong> (negative); <strong>Local net</strong> = cumulative (local added − density avoided); <strong>Net (statewide)</strong> = Statewide avoided + local added − density avoided.
          </p>
          <div className="transit-service-chart-container">
            <Line
              data={{
                labels: lineLabels,
                datasets: [
                  {
                    label: 'Statewide avoided',
                    data: cumStatewideAvoided,
                    borderColor: '#0369a1',
                    backgroundColor: 'rgba(3, 105, 161, 0.15)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Density avoided',
                    data: cumDensity,
                    borderColor: '#d97706',
                    backgroundColor: 'rgba(217, 119, 6, 0.12)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Added local emissions',
                    data: cumAdded,
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Local net',
                    data: cumLocalNet,
                    borderColor: '#7c3aed',
                    backgroundColor: 'rgba(124, 58, 237, 0.12)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                  {
                    label: 'Net (statewide)',
                    data: cumNet,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    tension: 0.25,
                    fill: false,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                ...lineOptions,
                scales: {
                  ...lineOptions.scales,
                  y: {
                    ...lineOptions.scales?.y,
                    ticks: {
                      callback: (value) =>
                        typeof value === 'number'
                          ? (value >= 0 ? '' : '−') + tonsFormatter.format(Math.abs(value))
                          : value,
                    },
                  },
                },
                plugins: {
                  ...lineOptions.plugins,
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const full: Record<string, string> = {
                          'Statewide avoided': 'Statewide avoided (MT CO₂e)',
                          'Density avoided': 'Density avoided (MT CO₂e)',
                          'Added local emissions': 'Added local emissions (MT CO₂e)',
                          'Local net': 'Local net (MT CO₂e)',
                          'Net (statewide)': 'Net statewide (MT CO₂e)',
                        };
                        const name = full[ctx.dataset.label as string] ?? ctx.dataset.label;
                        const y = ctx.parsed.y != null ? Number(ctx.parsed.y) : null;
                        const fmt =
                          y != null ? (y >= 0 ? '' : '−') + tonsFormatter.format(Math.abs(y)) : '—';
                        return `${name}: ${fmt}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactDevelopmentCharts;
